import { Divider, Tooltip, Typography } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import type { TSidebarItem } from '../../../../types/ux/sidebarItem.ux.type';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux.hook';
import {
  layoutSelector,
  onChangePageTitle,
} from '../../../../app/features/layout/layout.slice';
import { EMPTY_TEXT } from '../../../../utils/enum/defaultParams.enum';
import {
  AppRoutes,
  RedirectRoutes,
} from '../../../../utils/mapping/appRoutes.mapping';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../../app/features/auth/auth.slice'; // Ajusta la ruta a tu auth.slice

type Props = {
  option: TSidebarItem;
};

const SidebarItem = ({ option }: Props) => {
  const location = useLocation();
  const user = useSelector(selectCurrentUser);
  const query = new URLSearchParams(location.search);
  const fromSumary = query.has('fromSumary');
  const dispatch = useAppDispatch();
  const { openSidebar: open } = useAppSelector(layoutSelector);

  const isActiveCondition =
    (location.pathname === AppRoutes.recordScheduledVisits &&
      option.link === AppRoutes.scheduledVisits) ||
    (location.pathname === RedirectRoutes.createTicket &&
      option.link === AppRoutes.myTickets) ||
    (location.pathname === RedirectRoutes.myRequests &&
      option.link === AppRoutes.requests) ||
    (location.pathname === RedirectRoutes.myRequestsSumary &&
      option.link === AppRoutes.requestsSumary) ||
    (location.pathname === RedirectRoutes.myConsolidatedMaterials &&
      option.link === AppRoutes.consolidatedMaterials) ||
    (location.pathname === RedirectRoutes.myConsolidatedRecipe &&
      option.link === AppRoutes.consolidatedRecipe) ||
    (location.pathname === RedirectRoutes.myConsolidatedVersion &&
      option.link === AppRoutes.consolidatedVersion) ||
    (location.pathname === RedirectRoutes.creationRoute &&
      !fromSumary &&
      option.link === AppRoutes.requests) ||
    (location.pathname === RedirectRoutes.creationRoute &&
      fromSumary &&
      option.link === AppRoutes.requestsSumary) ||
    (location.pathname === RedirectRoutes.newRequest &&
      fromSumary &&
      option.link === AppRoutes.requestsSumary) ||
    (location.pathname === RedirectRoutes.newRequest &&
      !fromSumary &&
      option.link === AppRoutes.requests) ||
    (location.pathname === RedirectRoutes.myTypeMaterials &&
      option.link === AppRoutes.typeMaterials) ||
    (location.pathname === RedirectRoutes.myLoadMaterial &&
      option.link === AppRoutes.loadMaterial) ||
    (location.pathname === RedirectRoutes.myResources &&
      option.link === AppRoutes.resources) ||
    (location.pathname === RedirectRoutes.mySpeedTrefilado &&
      option.link === AppRoutes.speedTrefilado) ||
    (location.pathname === RedirectRoutes.mySpeedPlastificado &&
      option.link === AppRoutes.speedPlastificado) ||
    (location.pathname === RedirectRoutes.mySpeedVarilla &&
      option.link === AppRoutes.speedVarilla) ||
    (location.pathname === RedirectRoutes.mySpeedAlambreGalvanizado &&
      option.link === AppRoutes.speedAlambreGalvanizado) ||
    (location.pathname === RedirectRoutes.mySkuMallaOlimpica &&
      option.link === AppRoutes.skuMallaOlimpica) ||
    (location.pathname === RedirectRoutes.mySkuMallaOlimpicaPlastificada &&
      option.link === AppRoutes.skuMallaOlimpicaPlastificada) ||
    (location.pathname === RedirectRoutes.myIndicators &&
      option.link === AppRoutes.indicators);

  useEffect(() => {
    if (isActiveCondition && !option.targetBlank) {
      let titleToSet = option.label;
      let iconToSet = option.icon;

      // Lógica para el botón "Regresar"
      if (option.label === 'Regresar' && option.redirectToLabel) {
        titleToSet = option.redirectToLabel;
      }

      dispatch(onChangePageTitle({ title: titleToSet, icon: iconToSet }));
    }
  }, [location.pathname, isActiveCondition, dispatch, option]);

  const allowedUsers = ['eduar.huaman', 'miguel.flower'];
  if (option.id === 100 && !allowedUsers.includes(user?.username || '')) {
    return null;
  }
  const borderCondition = open && isActiveCondition;

  return (
    <Tooltip title={open ? EMPTY_TEXT : option.label} placement="right">
      <motion.div
        className="w-64"
        animate={{ width: open ? '14rem' : 'auto' }}
        transition={{ duration: !open ? 0 : 0.5 }}
      >
        {option.hasPrevDivider && <Divider className="my-3" />}
        {option.link && (
          <NavLink
            to={option.link}
            target={option.targetBlank ? '_blank' : '_self'}
            className={`
              sidebar-item
              ${isActiveCondition ? 'active-nav' : 'default-nav'} 
              ${borderCondition ? 'border-r-4' : 'border-r-0'}
            `}
          >
            <div className="mr-2">
              <i className={`far fa-${option.icon} fa-fw`} />
            </div>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Typography.Text ellipsis>{option.label}</Typography.Text>
                </motion.div>
              )}
            </AnimatePresence>
          </NavLink>
        )}
        {option.children &&
          option.children.map((child) => (
            <div className="sidebar-item default-nav">
              <div className="mr-2">
                <i className={`far fa-${child.icon} fa-fw`} />
              </div>
              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Typography.Text ellipsis>{child.label}</Typography.Text>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        {option.hasDivider && <Divider className="my-3" />}
      </motion.div>
    </Tooltip>
  );
};

export default SidebarItem;
